import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { LuCompass, LuLayers, LuMessageCircleQuestion } from "react-icons/lu";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone } from "../controller/global";
import { submitLead } from "../controller/leads";

// Same trust-logo set shown on /generative-ai-development, /about,
// /our-core-team, and /how-we-work-and-function.
const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// Categorised, real-fact-only FAQ — every answer restates something already
// stated elsewhere on the real site (About.jsx, Services.jsx, the Custom
// Software engagement models, the Career/Core Team hiring process, the
// How We Work process page). No new claims or invented pricing.
const FAQ_CATEGORIES = [
  {
    slug: "about-inqmise",
    label: "About InQmise",
    items: [
      { q: "Is InQmise Technologies a registered company?", a: "Yes — InQmise Technologies India Pvt Ltd is an MSME-registered IT company headquartered in Delhi NCR, India." },
      { q: "Where is InQmise Technologies located?", a: "Our office is at Cabin No. 10, Second Floor, A 24/9, Mohan Cooperative, Badarpur, New Delhi 110044." },
      { q: "What makes InQmise different from other IT companies?", a: "We combine technical excellence with business acumen — every team member, not just senior leadership, plays a vital role in delivery, and every engagement follows the same disciplined process from start to finish." },
    ],
  },
  {
    slug: "services",
    label: "Services We Offer",
    items: [
      { q: "What services does InQmise Technologies offer?", a: "Web development, mobile app development, UI/UX design, e-commerce, cloud and DevOps, cybersecurity, API development, and AI development — see our full Services page for the complete list." },
      { q: "Do you build AI-powered applications?", a: "Yes — we have dedicated services for Generative AI Development, Agentic AI Development, Recommendation Engine Development, and AI Integration Services, each with its own page describing what we build." },
      { q: "Do you offer support after a project launches?", a: "Yes — our Product Support & Maintenance service covers ongoing updates, bug fixes, and 24/7 assistance so your product keeps running smoothly after launch." },
    ],
  },
  {
    slug: "process",
    label: "Our Process",
    items: [
      { q: "What does your development process look like?", a: "Every project follows the same seven stages — from gathering requirements through design, development, testing, launch, and ongoing maintenance. See How We Work and Function for the full breakdown." },
      { q: "Do smaller projects skip any stages?", a: "No — every engagement, from a five-week MVP to a multi-quarter build, follows the same seven stages so nothing gets skipped." },
      { q: "What technologies do you build with?", a: "Our stack spans React, Next.js, Angular, and Vue on the front end; Node.js, Django, and .NET on the back end; MongoDB, MySQL, and PostgreSQL for data; and Docker, Kubernetes, and CI/CD pipelines for deployment." },
    ],
  },
  {
    slug: "engagement-pricing",
    label: "Engagement & Pricing",
    items: [
      { q: "What engagement models do you offer?", a: "Three: a Fixed-Scope Project for a clearly defined deliverable, a Dedicated Team for ongoing, evolving product work, and an MVP Sprint for validating an idea fast." },
      { q: "How is pricing structured?", a: "It depends on the model — fixed-scope work is priced and scheduled upfront with milestone-based payments, while a dedicated team engagement flexes with your changing priorities. We'll recommend the right fit once we understand your project." },
    ],
  },
  {
    slug: "careers",
    label: "Careers & Hiring",
    items: [
      { q: "How do I apply for a job at InQmise?", a: "Submit your CV through our Career page. From there, our process is four stages: CV submission, a phone screening, an on-site skill assessment, and a final interview with our CEO and HR executive." },
      { q: "What does InQmise look for in candidates?", a: "People who value collaboration over hierarchy, take ownership of their role, and care about clean, maintainable work — the same standards described on our Core Team page." },
    ],
  },
  {
    slug: "getting-started",
    label: "Getting Started",
    items: [
      { q: "How do I start a project with InQmise Technologies?", a: "Reach out through our contact form or schedule a free consultation, and our team will get back to you within one business day." },
      { q: "What happens after I submit an inquiry?", a: "Our team reviews your requirements and reaches out to schedule a discovery conversation — the first stage of our delivery process." },
    ],
  },
];

const TOTAL_QUESTIONS = FAQ_CATEGORIES.reduce((sum, cat) => sum + cat.items.length, 0);

const APPROACH = [
  {
    num: "01 / BROWSE",
    title: "Browse by topic, not by scrolling.",
    copy: "Six categories group every question we hear, so you can jump straight to what you need.",
    tags: ["Company & services", "Process & pricing", "Careers & getting started"],
  },
  {
    num: "02 / SCAN",
    title: "Scan real questions, not marketing copy.",
    copy: "Every question here is one we've actually been asked by a prospective client.",
    tags: [`${TOTAL_QUESTIONS} real questions`, "Plain answers", "No jargon"],
  },
  {
    num: "03 / EXPAND",
    title: "Expand for the full answer.",
    copy: "Click any question to expand the full answer, and collapse it again when you're done.",
    tags: ["One-click expand", "No page reload", "Still stuck? Just ask"],
  },
];

const HOW_TO_USE = [
  { Icon: LuCompass, t: "Browse by topic", d: "Jump to any of the six categories below using the quick links." },
  { Icon: LuLayers, t: "Scan the questions", d: "Each category groups two to three of the most common questions we hear." },
  { Icon: LuMessageCircleQuestion, t: "Expand for detail", d: "Click a question to expand the full answer — collapse it again when you're done." },
  { Icon: LuMessageCircleQuestion, t: "Still stuck?", d: "Reach out directly and we'll get back to you within one business day." },
];

const TOPIC_CARDS = FAQ_CATEGORIES.map((cat, i) => ({
  slug: cat.slug,
  title: cat.label,
  copy: `${cat.items.length} question${cat.items.length > 1 ? "s" : ""} about ${cat.label.toLowerCase()}.`,
  featured: i === 0,
}));

const ANSWER_CRITERIA = [
  { a: "Is InQmise a real registered company?", b: "COMPANY" },
  { a: "What services do you actually offer?", b: "SERVICES" },
  { a: "How is my project priced?", b: "PRICING" },
  { a: "How do I get started?", b: "START" },
];

const FACTS = [
  { a: String(TOTAL_QUESTIONS), b: "Questions answered" },
  { a: String(FAQ_CATEGORIES.length), b: "Topics covered" },
  { a: "1", b: "Business day response time" },
  { a: "0", b: "New claims invented" },
];

const ENGAGEMENT_RESULTS = [
  { a: "Fixed-Scope", b: "Clearly defined deliverable" },
  { a: "Dedicated Team", b: "Ongoing, evolving work" },
  { a: "MVP Sprint", b: "Validate an idea fast" },
  { a: "7", b: "Stage process, every model" },
];

const WHO_ASKS = [
  { a: "Founders exploring a build", b: "Weighing whether InQmise is the right technology partner." },
  { a: "Enterprise buyers", b: "Doing diligence before a larger engagement." },
  { a: "Candidates considering a role", b: "Checking our hiring process before applying." },
  { a: "Existing clients", b: "Double-checking how a stage or process step works." },
  { a: "Agencies exploring partnership", b: "Looking for extra engineering capacity." },
  { a: "Press & partners", b: "Verifying our registration and company basics." },
  { a: "Students & researchers", b: "Curious about our process and tech stack." },
  { a: "Just curious", b: "Browsing before reaching out with a specific question." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Same section-for-section structure as /generative-ai-development, /about,
// /our-core-team, and /how-we-work-and-function — followed by the real
// categorized Q&A accordion, since that's this page's actual reason for
// existing, then the contact CTA.
const AboutFAQ = () => {
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openKey, setOpenKey] = useState(null);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "FAQ | InQmise Technologies";
    const description = document.createElement("meta");
    description.name = "description";
    description.content = "Answers on how we scope, price and deliver software projects \u2014 engagement models, timelines, team structure and what working with us looks like.";
    document.head.appendChild(description);

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ_CATEGORIES.flatMap((cat) => cat.items).map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(schemaScript);
  }, []);

  const jumpTo = (slug) => {
    document.getElementById(slug)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your question.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Common questions · Answers before you ask</p>
          <h1>
            Answers to the <em>questions</em> we hear most.
          </h1>
          <p className="ea-lede">
            Organized by topic so you can jump straight to what you're looking for — company, services, process, pricing, careers, or getting started.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to get in touch">
              Get in touch ↗
            </button>
            <a className="ea-text-link" href="#topics">See all topics</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / ANSWER ENGINE <b>LIVE</b></div>
          <h2>Every answer, already true elsewhere on this site.</h2>
          <div className="ea-query"><small>COVERAGE</small>{TOTAL_QUESTIONS} real questions across {FAQ_CATEGORIES.length} topics — company, services, process, pricing, careers, and getting started.</div>
          <div className="ea-query"><small>RESPONSE</small>One business day, if your question isn't already covered below.</div>
          <div className="ea-flow"><b>Ask</b><i>→</i><b>Search</b><i>→</i><b>Answer</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* HOW TO USE THIS PAGE — feature rows */}
      <section className="ea-wrap ea-section" id="topics">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How to use this page</div>
            <h2>Built to get you<br />a straight answer.</h2>
          </div>
          <p>No contact form required to find out the basics.</p>
        </Reveal>

        {APPROACH.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      {/* FIND YOUR ANSWER — process grid */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Four steps</div>
            <h2>Find your answer<br />in four steps.</h2>
          </div>
          <p>Built to get you a straight answer without a contact form.</p>
        </Reveal>
        <div className="ea-process">
          {HOW_TO_USE.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-step" key={step.t}>
              <span>{String(i + 1).padStart(2, "0")}</span>
              <i><step.Icon aria-hidden="true" /></i>
              <h3>{step.t}</h3>
              <p>{step.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BROWSE BY TOPIC — service cards, doubling as navigation */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Browse by topic</div>
              <h2>Six places<br />to start.</h2>
            </div>
            <p>Jump straight to the cluster of questions closest to yours.</p>
          </Reveal>
          <div className="ea-cards">
            {TOPIC_CARDS.map((item, i) => (
              <Reveal
                as="button"
                i={(i % 4) + 1}
                reduceMotion={reduceMotion}
                key={item.slug}
                className={`ea-card ${item.featured ? "ea-featured" : ""}`}
                onClick={() => jumpTo(item.slug)}
                style={{ textAlign: "left", cursor: "pointer", font: "inherit" }}
              >
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHERE TO FIND THE ANSWER + FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Where to look</div>
              <h2>Match your question<br />to a topic.</h2>
            </div>
            <p>The most common shapes of question, and where they're answered.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>Common questions, sorted</h3>
              {ANSWER_CRITERIA.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
            <div className="ea-metrics">
              {FACTS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MOST ASKED — case-study-shaped spotlight */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Most asked</div>
            <h2>What engagement<br />models do you offer?</h2>
          </div>
          <p>The single most common question we get before a project starts.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">ENGAGEMENT & PRICING</div>
            <h3>Three models, one process.</h3>
            <p>A Fixed-Scope Project for a clearly defined deliverable, a Dedicated Team for ongoing, evolving product work, and an MVP Sprint for validating an idea fast. Every model follows the same seven-stage process.</p>
            <button className="ea-text-link" onClick={() => jumpTo("engagement-pricing")}>See all pricing questions</button>
          </div>
          <div className="ea-results">
            {ENGAGEMENT_RESULTS.map((r) => (
              <div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* WHO ASKS THESE QUESTIONS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Who asks these questions</div>
            <h2>Built for every kind<br />of visitor.</h2>
          </div>
          <p>Whoever you are, the goal is the same: a straight answer, fast.</p>
        </Reveal>
        <div className="ea-industries">
          {WHO_ASKS.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-industry" key={item.a}>
              <b>{item.a}</b>
              <p>{item.b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* THE REAL CATEGORIZED Q&A */}
      {FAQ_CATEGORIES.map((cat) => (
        <section
          className="ea-wrap ea-section"
          id={cat.slug}
          key={cat.slug}
          style={{ scrollMarginTop: 100, maxWidth: 820, margin: "0 auto" }}
        >
          <Reveal as="div" reduceMotion={reduceMotion} style={{ marginBottom: 20 }}>
            <p className="ea-eyebrow">{cat.label}</p>
          </Reveal>
          <div>
            {cat.items.map((item, i) => {
              const key = `${cat.slug}-${i}`;
              const isOpen = openKey === key;
              const panelId = `faq-panel-${key}`;
              const buttonId = `faq-button-${key}`;
              return (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-faq-row" key={item.q}>
                  <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                    <button
                      id={buttonId}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenKey(isOpen ? null : key)}
                    >
                      {item.q}<span>{isOpen ? "−" : "+"}</span>
                    </button>
                  </h3>
                  <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
                </Reveal>
              );
            })}
          </div>
        </section>
      ))}

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="faq-cta">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">Still have questions?</div>
            <h2 id="faq-cta">Get in touch with us.</h2>
            <p>Reach out through the form and our team will get back to you within one business day.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Ask us directly.</h3>
            <p>Tell us what you'd like to know and we'll get back to you within one business day.</p>

            <form onSubmit={handleContactSubmit} aria-label="FAQ page question form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="faq-fullname">Full Name</label>
                <input
                  id="faq-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "faq-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="faq-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="faq-phone">Phone Number (optional)</label>
                  <input
                    id="faq-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "faq-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="faq-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="faq-email">Email</label>
                  <input
                    id="faq-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "faq-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="faq-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="faq-subject">Subject</label>
                <input
                  id="faq-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., A question about pricing"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "faq-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="faq-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="faq-description">Your Question</label>
                <textarea
                  id="faq-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "faq-description-error" : undefined}
                />
                {contactErrors.description && <p id="faq-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Send my question ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default AboutFAQ;
